'use client';

import { useCallback, useEffect, useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { useUnit } from 'effector-react';
import { Icon, IconsMap } from '@nx-frontend/shared/ui/icon';
import { useRouter } from '@nx-frontend/shared/hooks/router-proxy';
import { setWorkspaceByIdEvent } from '@nx-frontend/shared/store/current-workspace';
import {
  $duplicateFolder,
  DuplicateFolderStatus,
  updateDuplicateFolderStateEvent,
  loadDuplicateFolderStateFx,
  DuplicateFolderState,
  resetDuplicateFolderStateEvent,
} from '@nx-frontend/shared/store/duplicate-folder';
import { setSidebarFetchMenuTreeEvent } from '@nx-frontend/shared/store/sidebar';
import { clearBreadcrumbsSpecialPagesVisitedEvent } from '@nx-frontend/shared/store/webnotes-breadcrumbs';

export const DuplicateFolderProgress: React.FC = () => {
  const [duplicateFolder] = useUnit([$duplicateFolder]);
  const [
    updateDuplicateFolderState,
    loadDuplicateFolderState,
    resetDuplicateFolderState,
    setWorkspaceById,
    setSidebarFetchMenuTree,
    clearBreadcrumbsSpecialPagesVisited,
  ] = useUnit([
    updateDuplicateFolderStateEvent,
    loadDuplicateFolderStateFx,
    resetDuplicateFolderStateEvent,
    setWorkspaceByIdEvent,
    setSidebarFetchMenuTreeEvent,
    clearBreadcrumbsSpecialPagesVisitedEvent,
  ]);

  const showCancelButton = false;
  const router = useRouter();

  const t = useTranslations('webnotes');

  const percent = useMemo(() => {
    if (duplicateFolder.total === 0) return 0;
    return Math.floor((duplicateFolder.current / duplicateFolder.total) * 100);
  }, [duplicateFolder]);

  const title = useMemo(() => {
    if (duplicateFolder.status === DuplicateFolderStatus.COMPLETED)
      return t('webnotes-item_is_duplicated', { itemName: duplicateFolder.folderName });
    return t('webnotes-duplicating_in_progress');
  }, [duplicateFolder.status, duplicateFolder.folderName, t]);

  const handleOpenFolderClick = useCallback(() => {
    setWorkspaceById(duplicateFolder.workspaceId);
    setSidebarFetchMenuTree(true);
    clearBreadcrumbsSpecialPagesVisited();
    updateDuplicateFolderState({ status: DuplicateFolderStatus.NONE });
    router.push(`/space/${duplicateFolder.workspaceId}/folder/${duplicateFolder.copyGlobalId}`);
  }, [
    router,
    duplicateFolder.copyGlobalId,
    duplicateFolder.workspaceId,
    updateDuplicateFolderState,
    setWorkspaceById,
    setSidebarFetchMenuTree,
    clearBreadcrumbsSpecialPagesVisited,
  ]);

  /**
   * Hide the progress bar after 15 seconds if in local storage the status is still in progress
   */
  useEffect(() => {
    let TID: NodeJS.Timeout | number | null = null;
    loadDuplicateFolderState().then((state: DuplicateFolderState) => {
      if (state.status === DuplicateFolderStatus.PROGRESS) {
        state.TID && clearTimeout(state.TID);
        TID = setTimeout(() => {
          resetDuplicateFolderState();
        }, 15000);
        updateDuplicateFolderState({
          TID,
        });
      }
    });
    return () => {
      TID && clearTimeout(TID);
    };
  }, [loadDuplicateFolderState, resetDuplicateFolderState, updateDuplicateFolderState]);

  if (duplicateFolder.status === DuplicateFolderStatus.NONE) {
    return null;
  }

  return (
    <div className="flex z-[100] fixed bottom-[40px] right-[80px] rounded border border-solid border-neutral-200 bg-white shadow-lg">
      <div className="flex-col items-start gap-[6px] relative w-[250px] py-4 px-[24px]">
        <div className="flex flex-col items-start gap-[1px] self-stretch">
          <div className="text-neutral-800 text-[13px] font-bold">{title}</div>
          <div className="text-neutral-800 text-[10px] ">
            {t('webnotes-duplicating_progress', {
              current: duplicateFolder.current,
              total: duplicateFolder.total,
              percent,
            })}
          </div>
          <div className="w-full h-[4px] items-start rounded-[30px] bg-neutral-200">
            <div className="h-full bg-teal-500 rounded-[30px] transition-all" style={{ width: percent + '%' }}></div>
          </div>
          {duplicateFolder.status === DuplicateFolderStatus.COMPLETED ? (
            <button
              data-cy="open-duplicated-folder"
              data-copyGlobalId={duplicateFolder.copyGlobalId}
              onClick={handleOpenFolderClick}
              className="h-[26px] py-[5px] pr-[12px] items-center text-cyan-900 text-[13px] font-bold"
            >
              {t(`webnotes-open_${duplicateFolder.parentId === 'root' ? 'folder' : 'subfolder'}`)}
            </button>
          ) : null}
        </div>
        {showCancelButton ? (
          <Icon
            icon={IconsMap.CLOSE}
            className="absolute text-[16px] text-neutral-500 top-[8px] right-[16px] cursor-pointer"
          />
        ) : null}
      </div>
    </div>
  );
};
